import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/20/solid';
import type { StorageProvider } from '../../services/storageService';
import { ProviderIcon, providerLabel } from './storageProviderUi';
import CassetteLoader from '../CassetteLoader';

export type StorageTileMode = 'settings' | 'onboarding';

export interface StorageProviderTileProps {
  provider: StorageProvider;
  mode: StorageTileMode;
  linked: boolean;
  isProjectStorage: boolean;
  accountEmail?: string;
  busy?: boolean;
  highlighted?: boolean;
  defaultSelected?: boolean;
  savingDefault?: boolean;
  cassetteEligible?: boolean;
  eligibleForProjectStorage?: boolean;
  onConnect: () => void;
  onDisconnect?: () => void;
  onMoveProjectsHere?: () => void;
  onSelectDefault?: () => void;
}

const StorageProviderTile: React.FC<StorageProviderTileProps> = ({
  provider,
  mode,
  linked,
  isProjectStorage,
  accountEmail,
  busy = false,
  highlighted = false,
  defaultSelected = false,
  savingDefault = false,
  cassetteEligible: _cassetteEligible = true,
  eligibleForProjectStorage = true,
  onConnect,
  onDisconnect,
  onMoveProjectsHere,
  onSelectDefault,
}) => {
  const { t } = useTranslation();
  const label = providerLabel(provider, t);
  const isCassette = provider === 'cassette_s3';
  const compact = mode === 'settings';
  const connectDisabled = busy;
  /** Linked Cassette Cloud in settings stays visually active; clicks route to paywall or transfer. */
  const cassetteLinkedInSettings = compact && isCassette && linked;
  const iconDimmed = connectDisabled && !busy && !cassetteLinkedInSettings && !isCassette;

  const handleIconClick = () => {
    if (connectDisabled) return;
    if (mode === 'settings') {
      onConnect();
      return;
    }
    if (!linked) {
      onConnect();
      return;
    }
    if (onSelectDefault && !defaultSelected) {
      onSelectDefault();
    }
  };

  const iconStateClass = linked
    ? 'ring-2 ring-[var(--brand-from)] bg-[var(--brand-from)]/10 opacity-100'
    : 'opacity-45 hover:opacity-80 ring-1 ring-[var(--color-border)] bg-[var(--color-hover)]/40';

  const tileBorderClass = linked
    ? 'border-[var(--brand-from)]'
    : highlighted
      ? 'ring-2 ring-amber-500 border-[var(--color-border)]'
      : 'border-[var(--color-border)]';

  if (compact) {
    return (
      <div
        className={`relative rounded-lg border p-2.5 flex flex-col items-center text-center gap-1.5 bg-[var(--color-surface)] ${tileBorderClass}`}
        data-testid={`storage-tile-${provider}`}
      >
        {onDisconnect && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDisconnect();
            }}
            disabled={busy}
            title={t('settings.storage.disconnect')}
            aria-label={t('settings.storage.disconnect')}
            className="absolute top-1 right-1 p-0.5 rounded text-[var(--color-muted)] hover:text-red-600 hover:bg-[var(--color-hover)] disabled:opacity-50"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        )}

        {busy && (
          <div className="absolute inset-0 z-[1] flex items-center justify-center rounded-lg bg-[var(--color-surface)]/75">
            <CassetteLoader size={28} variant="inline" />
          </div>
        )}

        <button
          type="button"
          onClick={handleIconClick}
          disabled={iconDimmed}
          data-testid={linked ? `storage-icon-${provider}` : `connect-storage-${provider}`}
          title={
            cassetteLinkedInSettings
              ? isProjectStorage
                ? t('settings.storage.projectStorageActive', { provider: label })
                : label
              : connectDisabled && isCassette && !eligibleForProjectStorage
              ? t('settings.storage.cassetteCloudPaywallTitle', 'Upgrade for Cassette Cloud project storage')
              : connectDisabled && isCassette
              ? t('settings.storage.transferProjects', 'Transfer project storage')
              : !linked
              ? t('settings.storage.connectHint', { provider: label })
              : isProjectStorage
                ? t('settings.storage.projectStorageActive', { provider: label })
                : t('settings.storage.reconnectHint', {
                    provider: label,
                    defaultValue: 'Reconnect {{provider}}',
                  })
          }
          className={`w-11 h-11 rounded-lg flex items-center justify-center transition-all ${
            iconDimmed ? 'disabled:cursor-not-allowed disabled:opacity-40' : connectDisabled ? 'cursor-not-allowed' : ''
          } ${iconStateClass}`}
        >
          <ProviderIcon provider={provider} className="w-6 h-6" />
        </button>

        <p className="text-xs font-medium text-[var(--color-text)] leading-tight truncate w-full px-0.5">
          {label}
        </p>

        {isProjectStorage && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[var(--brand-from)]/15 text-[var(--brand-from)] leading-none">
            {t('settings.storage.projectStorageBadge')}
          </span>
        )}

        {linked && accountEmail && (
          <p className="text-[10px] text-[var(--color-muted)] truncate w-full leading-tight" title={accountEmail}>
            {accountEmail}
          </p>
        )}

        {linked && isCassette && !isProjectStorage && !eligibleForProjectStorage && (
          <p className="text-[10px] text-[var(--color-muted)] leading-tight px-0.5">
            {t('settings.storage.cassetteCloudProjectStorageOnly')}
          </p>
        )}
      </div>
    );
  }

  // Onboarding: compact icon grid with default indicator
  return (
    <div
      className={`relative rounded-lg border p-2.5 flex flex-col items-center text-center gap-1.5 bg-[var(--color-surface)] ${tileBorderClass}`}
      data-testid={`storage-tile-${provider}`}
    >
      {busy && (
        <div className="absolute inset-0 z-[1] flex items-center justify-center rounded-lg bg-[var(--color-surface)]/75">
          <CassetteLoader size={28} variant="inline" />
        </div>
      )}

      <button
        type="button"
        onClick={handleIconClick}
        disabled={connectDisabled || (linked && savingDefault)}
        data-testid={`connect-storage-${provider}`}
        className={`w-11 h-11 rounded-lg flex items-center justify-center transition-all disabled:cursor-not-allowed disabled:opacity-40 ${iconStateClass} ${
          defaultSelected ? 'ring-2 ring-[var(--brand-from)]' : ''
        }`}
      >
        <ProviderIcon provider={provider} className="w-6 h-6" />
      </button>

      <p className="text-xs font-medium text-[var(--color-text)] leading-tight">{label}</p>

      {linked ? (
        <>
          {defaultSelected && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[var(--brand-from)]/15 text-[var(--brand-from)]">
              {t('settings.storage.projectStorageBadge')}
            </span>
          )}
          {accountEmail && (
            <p className="text-[10px] text-[var(--color-muted)] truncate w-full" title={accountEmail}>
              {accountEmail}
            </p>
          )}
          {!defaultSelected && onSelectDefault && (
            <button
              type="button"
              onClick={onSelectDefault}
              disabled={savingDefault}
              className="text-[10px] text-[var(--brand-from)] underline disabled:opacity-50"
            >
              {t('settings.storage.setAsProjectStorage')}
            </button>
          )}
          {isCassette && !defaultSelected && !onSelectDefault && !eligibleForProjectStorage && (
            <p className="text-[10px] text-[var(--color-muted)]">
              {t('settings.storage.cassetteCloudProjectStorageOnly')}
            </p>
          )}
        </>
      ) : (
        <p className="text-[10px] text-[var(--color-muted)]">{t('settings.storage.notConnected')}</p>
      )}

    </div>
  );
};

export default StorageProviderTile;
